/**
 * demo-journey — carries the active walkthrough across pages.
 *
 * Entry links set ?journey=<handle> (e.g. ?journey=core); it's mirrored into
 * sessionStorage so the journey survives clicks that drop the param (real app
 * buttons, detours, redirects). ?journey=exit clears it.
 *
 * The orienter section (a bar pinned to the bottom of every page) renders
 * one variant per demo_callout matching the page — journey steps (tagged
 * with data-journey and data-hash; a callout path may include #anchor,
 * letting two journey steps share one page), the page's standalone
 * callout, or a template-type default — and server-marks one as the
 * default (data-orienter-default). An inline script in the section
 * activates the right one and applies the persisted open/closed state
 * before first paint; this script repeats that choice on hashchange,
 * propagates the journey param on the back/next arrows and the step menu's
 * links, points any element marked data-demo-next at the journey's next
 * step, runs the step menu and the +/− toggle (closed by default — only the
 * visitor's own toggle opens it, and that choice persists in localStorage),
 * keeps the footer's reserved space equal to the bar's height, and narrows
 * the bar while the non-modal cart side drawer is open.
 */
(function () {
  var journey = null;
  try {
    var param = new URLSearchParams(location.search).get('journey');
    if (param === 'exit') {
      sessionStorage.removeItem('demo-journey');
    } else if (param) {
      sessionStorage.setItem('demo-journey', param);
      journey = param;
    } else {
      journey = sessionStorage.getItem('demo-journey');
    }
  } catch (e) {
    /* storage unavailable — param-only mode */
  }

  function withJourney(url) {
    if (!journey || !url || url.indexOf('journey=') !== -1) return url;
    var hash = '';
    var i = url.indexOf('#');
    if (i !== -1) {
      hash = url.slice(i);
      url = url.slice(0, i);
    }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'journey=' + encodeURIComponent(journey) + hash;
  }

  var variants = Array.prototype.slice.call(document.querySelectorAll('[data-orienter-variant]'));
  var active = null;
  var nextUrl = null;
  var OPEN_KEY = 'demo-orienter-open';
  /* keys from earlier orienter versions — no longer read, clear them */
  try {
    localStorage.removeItem('demo-orienter-collapsed');
    localStorage.removeItem('demo-orienter-dismissed');
  } catch (e) { /* ignore */ }
  function isOpen() {
    try { return localStorage.getItem(OPEN_KEY) === '1'; } catch (e) { return false; }
  }

  /* Footer reserves the bar's height so the page can scroll clear of it */
  function syncHeight() {
    var el = document.querySelector('[data-orienter-variant].is-active');
    var h = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--demo-orienter-height', h + 'px');
  }


  function pickVariant() {
    if (!variants.length) return;
    var hash = location.hash.replace('#', '');
    var candidates = journey
      ? variants.filter(function (v) {
          return v.getAttribute('data-journey') === journey;
        })
      : [];
    var chosen = null;
    if (candidates.length) {
      /* prefer a hash-specific variant when the hash matches; otherwise the
         journey's hash-less variant; otherwise the journey's first */
      chosen = candidates.filter(function (v) {
        return hash && v.getAttribute('data-hash') === hash;
      })[0]
        || candidates.filter(function (v) {
          return v.getAttribute('data-hash') === '';
        })[0]
        || candidates[0];
    }
    /* No active walkthrough, or none matching this page: the server-marked
       default (standalone callout, first journey step, or template
       default). Dismissed: nothing. */
    if (!chosen) {
      chosen = variants.filter(function (v) {
        return v.hasAttribute('data-orienter-default');
      })[0] || null;
    }
    active = chosen;
    var open = isOpen();
    variants.forEach(function (v) {
      v.classList.toggle('is-active', v === active);
      v.classList.toggle('is-open', v === active && open);
      var t = v.querySelector('[data-orienter-toggle]');
      if (t) t.setAttribute('aria-expanded', String(v === active && open));
    });
    syncHeight();
    if (!active) return;

    nextUrl = null;
    var next = active.querySelector('[data-journey-next]');
    if (next) {
      nextUrl = next.getAttribute('data-next-target') || next.getAttribute('href');
      next.setAttribute('data-next-target', nextUrl);
      next.setAttribute('href', withJourney(nextUrl));
    }
    var prev = active.querySelector('[data-journey-prev]');
    if (prev) {
      var prevUrl = prev.getAttribute('data-prev-target') || prev.getAttribute('href');
      prev.setAttribute('data-prev-target', prevUrl);
      prev.setAttribute('href', withJourney(prevUrl));
    }

    /* Step menu rows stay on the walkthrough the visitor is following */
    active.querySelectorAll('[data-journey-step]').forEach(function (el) {
      var stepUrl = el.getAttribute('data-step-target') || el.getAttribute('href');
      el.setAttribute('data-step-target', stepUrl);
      el.setAttribute('href', withJourney(stepUrl));
    });

    document.querySelectorAll('[data-demo-next]').forEach(function (el) {
      if (nextUrl) {
        el.setAttribute('href', withJourney(nextUrl));
      } else if (journey) {
        el.setAttribute('href', withJourney(el.getAttribute('href')));
      }
    });
  }

  pickVariant();
  window.addEventListener('hashchange', pickVariant);
  window.addEventListener('resize', syncHeight);
  if (typeof ResizeObserver !== 'undefined') {
    var ro = new ResizeObserver(syncHeight);
    variants.forEach(function (v) { ro.observe(v); });
  }

  /* Demo hook: arriving with #drawer (or #sidedrawer) opens the cart drawer (the drawer
     pattern step). If the cart is empty, one Box o' Holiday is seeded first
     so the drawer has something to show — once per session, then reload. */
  if (location.hash === '#drawer' || location.hash === '#sidedrawer') {
    var openDrawer = function () {
      var drawerEl = document.getElementById('cart-drawer');
      if (!drawerEl) return;
      var dlg = drawerEl.querySelector('dialog');
      drawerEl.setAttribute('open', '');
      if (dlg) dlg.setAttribute('open', '');
      drawerEl.dispatchEvent(new CustomEvent('theme-drawer:restoring'));
    };
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.item_count > 0) return openDrawer();
        var seeded = false;
        try { seeded = sessionStorage.getItem('demo-drawer-seeded') === '1'; } catch (e) { /* ignore */ }
        if (seeded) return openDrawer();
        return fetch('/products/box-o-holiday.js')
          .then(function (r) { return r.json(); })
          .then(function (p) {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: p.variants[0].id, quantity: 1 }),
            });
          })
          .then(function () {
            try { sessionStorage.setItem('demo-drawer-seeded', '1'); } catch (e) { /* ignore */ }
            location.reload();
          });
      })
      .catch(openDrawer);
  }

  /* The +/− toggle: open/close, persisted. Closed by default — no link
     opens the bar, only this button (Cam, Figma 2026-09-12). */
  variants.forEach(function (v) {
    var t = v.querySelector('[data-orienter-toggle]');
    if (t) {
      t.addEventListener('click', function () {
        var nowOpen = !v.classList.contains('is-open');
        try {
          if (nowOpen) localStorage.setItem(OPEN_KEY, '1'); else localStorage.removeItem(OPEN_KEY);
        } catch (e) { /* state just won't persist */ }
        variants.forEach(function (w) {
          w.classList.toggle('is-open', w === v && nowOpen);
          var tt = w.querySelector('[data-orienter-toggle]');
          if (tt) tt.setAttribute('aria-expanded', String(w === v && nowOpen));
        });
        syncHeight();
      });
    }
  });

  /* Step menu: the page title opens the list of the walkthrough's steps. */
  variants.forEach(function (v) {
    var trigger = v.querySelector('[data-orienter-menu-toggle]');
    var menu = v.querySelector('[data-orienter-menu]');
    if (!trigger || !menu) return;

    function setMenu(open) {
      menu.hidden = !open;
      trigger.setAttribute('aria-expanded', String(open));
      v.classList.toggle('has-menu-open', open);
    }

    trigger.addEventListener('click', function (event) {
      event.stopPropagation();
      setMenu(menu.hidden);
    });

    document.addEventListener('click', function (event) {
      if (!menu.hidden && !menu.contains(event.target) && !trigger.contains(event.target)) {
        setMenu(false);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !menu.hidden) {
        setMenu(false);
        trigger.focus();
      }
    });
  });

  /* Narrow the bar while the cart side drawer is open, so the drawer's
     buttons stay clear (Cam, Figma 2026-09-12). Only when the drawer is
     non-modal — below Horizon's 990px drawer breakpoint it opens with
     showModal() and its backdrop covers the bar anyway. */
  var cartDrawer = document.getElementById('cart-drawer');
  if (cartDrawer) {
    var syncDrawerInset = function () {
      var inset = 0;
      var panel = cartDrawer.querySelector('dialog');
      var closing = panel && panel.classList.contains('theme-drawer__dialog--closing');
      if (panel && panel.open && !closing) {
        var modal;
        try {
          modal = panel.matches(':modal');
        } catch (e) {
          modal = window.innerWidth < 990;
        }
        if (!modal) inset = Math.round(panel.getBoundingClientRect().width);
      }
      document.documentElement.style.setProperty('--demo-orienter-inset', inset + 'px');
    };

    if (typeof MutationObserver !== 'undefined') {
      var drawerObserver = new MutationObserver(syncDrawerInset);
      drawerObserver.observe(cartDrawer, { attributes: true, attributeFilter: ['open'] });
      var drawerPanel = cartDrawer.querySelector('dialog');
      if (drawerPanel) {
        drawerObserver.observe(drawerPanel, { attributes: true, attributeFilter: ['open', 'class'] });
      }
    }
    window.addEventListener('resize', syncDrawerInset);
    syncDrawerInset();
  }
})();
